import { FC, useState, useReducer, useRef, useEffect } from 'react'
import {
  Grid,
  TextField,
  Autocomplete,
  Button,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone'
import { useNavigate } from 'react-router-dom'
import ApplicationLinks from '../../../ApplicationLinks'

import { PrepareIssueItem, SelectOptions, EntryType } from '../../../services'

import CustomNumberField from '../Users/profile/CustomNumberField'
import {
  isOtherChargesCategory,
  isReusableOtherChargeOption,
} from './otherChargesChallan'

// Special option for "Add New Item" when no items are available
const ADD_NEW_ITEM_OPTION: SelectOptions = {
  id: -1,
  label: 'Add New Item',
  category: '',
}

export const HORZ_SPACING = 1.5

const RENT_CYCLE_STORAGE_KEY = 'issueChallanRentCycleByCategoryId'

type RentCycleMode = 'DAILY' | 'MONTHLY'

type RentCycleCacheEntry = {
  mode: RentCycleMode
}

function readRentCycleCache(categoryId: number): RentCycleCacheEntry | null {
  if (categoryId == null || Number(categoryId) <= 0) return null
  try {
    const raw = localStorage.getItem(RENT_CYCLE_STORAGE_KEY)
    if (!raw) return null
    const map = JSON.parse(raw) as Record<string, RentCycleCacheEntry>
    return map[String(categoryId)] ?? null
  } catch {
    return null
  }
}

function writeRentCycleCache(categoryId: number, entry: RentCycleCacheEntry) {
  if (categoryId == null || Number(categoryId) <= 0) return
  try {
    const raw = localStorage.getItem(RENT_CYCLE_STORAGE_KEY)
    const map = raw ? (JSON.parse(raw) as Record<string, RentCycleCacheEntry>) : {}
    map[String(categoryId)] = entry
    localStorage.setItem(RENT_CYCLE_STORAGE_KEY, JSON.stringify(map))
  } catch {
    /* ignore quota/parse */
  }
}

type IssueItemFields =
  | 'item'
  | 'quantity'
  | 'rate'
  | 'total_cost'
  | 'height'
  | 'width'
  | 'length'
type IssueItemFieldsState = Record<
  IssueItemFields,
  Partial<{
    value: string
    error: boolean
    helperText: string
  }>
>

type IssueItemErrors = Record<IssueItemFields, Record<string, string>>

const issueItemErrors: IssueItemErrors = {
  item: {
    empty: 'Select item',
  },
  quantity: {
    empty: 'Enter quantity',
    maxLimit: 'Only <QUANTITY> <ITEM> <TYPE>',
  },
  rate: {
    empty: 'Enter rate',
  },

  total_cost: {
    empty: 'Enter total cost',
  },
  height: {
    empty: 'Enter height',
  },
  width: {
    empty: 'Enter width',
  },
  length: {
    empty: 'Enter length',
  },
}

const initialItemIssueState: IssueItemFieldsState = {
  item: {
    error: false,
    helperText: ' ',
  },
  quantity: {
    error: false,
    helperText: ' ',
  },
  rate: {
    error: false,
    helperText: ' ',
  },
  total_cost: {
    error: false,
    helperText: ' ',
  },
  height: {
    error: false,
    helperText: ' ',
  },
  width: {
    error: false,
    helperText: ' ',
  },
  length: {
    error: false,
    helperText: ' ',
  },
}
const itemReducer = (state: Partial<IssueItemFieldsState>, action) => {
  const {
    type,
    payload: { name, value },
    helperText,
  } = action
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: helperText
            ? helperText
            : initialItemIssueState[name]?.helperText,
        },
      }
    case 'ERROR':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: issueItemErrors[name]['empty'],
        },
      }
    case 'ERROR_MAX_LIMIT':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: helperText,
        },
      }
    case 'RESET':
      return {
        ...initialItemIssueState,
        [name]: {
          ...state[name],
          error: false,
          value: value,
          helperText: initialItemIssueState[name]?.helperText,
        },
      }
    default:
      return state
  }
}

type ItemComponentProps = {
  selectOptions: SelectOptions[]
  entryType?: EntryType
  addItem: (item: PrepareIssueItem) => void
  submission_type?: 'LOST' | 'SUBMIT'
  billType: string
  defaultRateZero: boolean
  accountType?: string
  billInfo?: any
  onCloseDialog?: () => void
  selectedDate?: string
  quantityLabelOverride?: string
  suppressAvailableHelper?: boolean
  ignoreAvailableQuantity?: boolean
  /** Delivery challan: cycle-based rent (cycleRatePlan); standard lines only. */
  enableRentCyclePlan?: boolean
  /** Issue/Receive challans: OtherCharges lines use Charge Title + Amount (no qty / rent cycle). */
  otherChargesAmountUi?: boolean
}

const ItemComponent: FC<ItemComponentProps> = ({
  selectOptions,
  entryType = EntryType.OUT,
  addItem,
  submission_type = 'SUBMIT',
  billType,
  defaultRateZero,
  accountType,
  billInfo,
  onCloseDialog,
  selectedDate,
  quantityLabelOverride,
  suppressAvailableHelper = false,
  ignoreAvailableQuantity = false,
  enableRentCyclePlan = false,
  otherChargesAmountUi = false,
}) => {
  console.log(selectOptions)
  const navigate = useNavigate()

  const [item, setItem] = useState<PrepareIssueItem | null>()
  const [selectedItem, setSelectedItem] = useState<SelectOptions | null>()
  const [itemData, dispatch] = useReducer(itemReducer, initialItemIssueState)
  const [value, setValue] = useState<SelectOptions | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [entryCategroy, setEntryCategory] = useState<string | null>(null)
  const [rentCycleMode, setRentCycleMode] = useState<RentCycleMode>('DAILY')
  const [chargeTitle, setChargeTitle] = useState('')
  const [chargeTitleError, setChargeTitleError] = useState(false)
  const autocompleteRef = useRef(null)
  const keepGenericOtherChargeSelectedRef = useRef(false)

  const isPerSqftBill =
    (billInfo?.billType ?? billType) === 'PER_SQFT' ||
    Number(billInfo?.ratePerSqft) > 0

  const isOtherChargesChallanLine = Boolean(
    otherChargesAmountUi &&
      selectedItem &&
      isOtherChargesCategory(selectedItem.category)
  )

  const isGenericOtherChargeChallanLine = Boolean(
    isOtherChargesChallanLine && isReusableOtherChargeOption(selectedItem)
  )

  const showRentCycle = Boolean(
    enableRentCyclePlan &&
      entryType === EntryType.OUT &&
      submission_type === 'SUBMIT' &&
      selectedItem &&
      !selectedItem.isArea &&
      !selectedItem.isAreaV2 &&
      !isOtherChargesChallanLine
  )

  /** Rate prefilled when Daily (or non–rent-cycle); Monthly leaves rate empty for user entry. */
  const getAutofillRateForDaily = (source: { rate?: number } | null) => {
    if (!source) return null
    if (submission_type === 'LOST') return null
    if (defaultRateZero || billType === 'PER_SQFT') return 0
    return source.rate ?? null
  }

  const onRentCycleChange = (e: SelectChangeEvent<RentCycleMode>) => {
    const next = e.target.value as RentCycleMode
    setRentCycleMode(next)   
  }

  useEffect(() => {
    if (isPerSqftBill) {
      setRentCycleMode('DAILY')
    }
  }, [isPerSqftBill])

  const addItemHandler = () => {
    let error = false

    if (selectedItem?.isArea) {
      // IF item is of type Area them we have to do the empty check of height, width and length
      if (!item?.height || item.height <= 0) {
        error = true
        dispatch({ type: 'ERROR', payload: { name: 'height', value: null } })
      }

      if (!item?.width || item.width <= 0) {
        error = true
        dispatch({ type: 'ERROR', payload: { name: 'width', value: null } })
      }

      if (!item?.length || item.length <= 0) {
        error = true
        dispatch({ type: 'ERROR', payload: { name: 'length', value: null } })
      }
    } else {
      if (!item?.item?.id) {
        error = true
        dispatch({ type: 'ERROR', payload: { name: 'item', value: null } })
      }
      // Allow ADD when either quantity > 0 OR excessQuantity > 0
      const hasExcess =
        item?.excessQuantity != null && Number(item.excessQuantity) > 0
      if (
        !isOtherChargesChallanLine &&
        (!item?.quantity || item.quantity <= 0) &&
        !hasExcess
      ) {
        error = true
        dispatch({ type: 'ERROR', payload: { name: 'quantity', value: null } })
      }

      const useCycleRate = showRentCycle && rentCycleMode === 'MONTHLY'
      const needsDailyRate =
        (entryType === EntryType.OUT || submission_type === 'LOST') && !useCycleRate

      if (isOtherChargesChallanLine) {
        if (isGenericOtherChargeChallanLine && !chargeTitle?.trim()) {
          error = true
          setChargeTitleError(true)
        }
        if (!item?.rate && item?.rate !== 0) {
          error = true
          dispatch({ type: 'ERROR', payload: { name: 'rate', value: null } })
        }
      } else if (
        needsDailyRate &&
        billType !== 'PER_SQFT' &&
        !item?.rate &&
        item?.rate !== 0
      ) {
        error = true
        dispatch({ type: 'ERROR', payload: { name: 'rate', value: null } })
        dispatch({
          type: 'ERROR',
          payload: { name: 'total_cost', value: null },
        })
      }

      if (useCycleRate) {
        const charge = Number(item?.rate)
        if (!item?.rate && item?.rate !== 0) {
          error = true
          dispatch({ type: 'ERROR', payload: { name: 'rate', value: null } })
        } else if (Number.isNaN(charge) || charge <= 0) {
          error = true
          dispatch({ type: 'ERROR', payload: { name: 'rate', value: null } })
        }
      }
    }

    if (!error) {
      if (selectedItem?.isArea) {
        item.isArea = true
        item.quantity = 1 // We will not show the quantity on UI but pass the hardcoded value 1 to the API
      }
      if (selectedItem?.isAreaV2) {
        item.isAreaV2 = true
      }
      if (submission_type === 'LOST') {
        const itemtoAdd = { ...item }
        itemtoAdd.lostItemCost = itemtoAdd.rate
        delete itemtoAdd.rate
        itemtoAdd.rate = selectedItem?.rate
        addItem(itemtoAdd)
      } else {
        let toAdd: PrepareIssueItem = { ...item }
        delete toAdd.cycleRatePlan

        const useCycleRate = showRentCycle && rentCycleMode === 'MONTHLY'
        if (useCycleRate) {
          toAdd = {
            ...toAdd,
            rate: 0,
            cycleRatePlan: {
              cycleDays: 30,
              cycleCharge: Number(item.rate),
            },
          }
          if (selectedItem?.categoryId) {
            writeRentCycleCache(selectedItem.categoryId, {
              mode: rentCycleMode,
            })
          }
        } else if (selectedItem?.categoryId && showRentCycle) {
          writeRentCycleCache(selectedItem.categoryId, {
            mode: 'DAILY',
          })
        }

        if (isOtherChargesChallanLine) {
          toAdd.quantity = 1
          if (isGenericOtherChargeChallanLine) {
            const title = chargeTitle.trim()
            toAdd.chargeTitle = title
            toAdd.item = {
              ...toAdd.item,
              id: toAdd.item.id,
              label: title,
              category: selectedItem?.category,
            }
          }
          delete toAdd.siteTransferRate
        }

        addItem(toAdd)
      }

      const keepGenericOtherChargeSelected = isReusableOtherChargeOption(value)

      if (keepGenericOtherChargeSelected) {
        keepGenericOtherChargeSelectedRef.current = true
        setEntryCategory(null)
        setChargeTitle('')
        setChargeTitleError(false)
        setItem({
          entryType,
          rate: null,
          quantity: 0,
          item: {
            id: value.id,
            label: value.label,
            category: value.category,
          },
          excessQuantity: undefined,
        })
        dispatch({ type: 'RESET', payload: { name: 'item', value: value.id } })
        dispatch({ type: 'SET', payload: { name: 'rate', value: '' } })
      } else {
        setItem({
          entryType,
          rate: null,
          item: null,
          quantity: null,
          excessQuantity: undefined,
        })
        setChargeTitle('')
        setChargeTitleError(false)
        setValue(null)
        setEntryCategory(value?.category ?? null)
      }
    }
  }

  useEffect(() => {
    if (selectOptions) {
      if (keepGenericOtherChargeSelectedRef.current) {
        keepGenericOtherChargeSelectedRef.current = false
        const otherChargeOpt = selectOptions.find((opt) =>
          isReusableOtherChargeOption(opt)
        )
        if (otherChargeOpt) {
          setValue(otherChargeOpt)
          setInputValue(otherChargeOpt.label ?? '')
          setSelectedItem(otherChargeOpt)
          dispatch({
            type: 'RESET',
            payload: { name: 'item', value: otherChargeOpt.id },
          })
        }
        dispatch({
          type: 'SET',
          payload: { name: 'quantity', value: 0 },
          helperText: '',
        })
        dispatch({ type: 'SET', payload: { name: 'rate', value: '' } })
        setTimeout(() => chargeTitleTextFieldRef.current?.focus(), 0)
      } else {
        setValue(null)
        setInputValue('')
        dispatch({
          type: 'SET',
          payload: { name: 'quantity', value: 0 },
          helperText: '',
        })
      }
    }
    if (entryCategroy) {
      let hasMoreItems = false
      for (let i = 0; i < selectOptions.length; i++) {
        const option = selectOptions[i].label.toLowerCase()
        if (option.startsWith(entryCategroy.toLowerCase() + ' ')) {
          setInputValue(entryCategroy + ' ')
          hasMoreItems = true
          break
        }
      }
      if (!hasMoreItems) {
        setInputValue('')
      }
      autocompleteRef.current.focus()
      setEntryCategory(null)
    }
  }, [selectOptions])

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      // Enter key
      addItemHandler()
      event.preventDefault()
      event.stopPropagation()
       // Stop all other Enter key handlers
    }
  }

  const onInputChange = (e) => {
    const { name } = e.target
    let { value } = e.target

    // For Lost Item dialog, force Total Cost (rate field) to integer only
    if (submission_type === 'LOST' && name === 'rate') {
      if (value === '' || value === null || value === undefined) {
        value = ''
      } else {
        const parsed = parseInt(value, 10)
        value = Number.isNaN(parsed) ? '' : `${parsed}`
      }
    }
    const remainingCap = selectedItem?.availableQuantity
    const parsedQty =
      name === 'quantity' && value !== '' && value != null
        ? Number(value)
        : NaN
    const canSplitReceiveExcess =
      name === 'quantity' &&
      !ignoreAvailableQuantity &&
      selectedItem &&
      accountType !== 'ITEMS_TAKEN' &&
      entryType === EntryType.IN &&
      submission_type !== 'LOST' &&
      !selectedItem.isArea &&
      !Number.isNaN(parsedQty) &&
      remainingCap != null &&
      Number.isFinite(Number(remainingCap)) &&
      parsedQty > Number(remainingCap)

    if (canSplitReceiveExcess) {
      const remaining = Number(remainingCap)
      const overflow = parsedQty - remaining
      setItem((prev) => {
        const prevExcess = Number(prev?.excessQuantity) || 0
        return {
          ...prev,
          quantity: remaining,
          excessQuantity: prevExcess + overflow,
        }
      })
      dispatch({
        type: 'SET',
        payload: { name: 'quantity', value: remaining },
        helperText: suppressAvailableHelper
          ? initialItemIssueState.quantity.helperText
          : `${selectedItem.availableQuantity} ${
              selectedItem.isAreaV2
                ? 'sqft available'
                : selectedDate
                ? `remaining (as of ${selectedDate})`
                : 'remaining (as of selected date)'
            }`,
      })
      return
    }

    setItem((item) => {
      return {
        ...item,
        [name]: value,
      }
    })
    if (value && name) {
      if (
        !ignoreAvailableQuantity &&
        name == 'quantity' &&
        selectedItem &&
        value > selectedItem.availableQuantity && accountType !== 'ITEMS_TAKEN'
      ) {
        const helperText = issueItemErrors[name]['maxLimit']
          .replace('<QUANTITY>', selectedItem.availableQuantity)
          .replace(
            '<ITEM>',
            `${selectedItem.availableQuantity > 1 ? 'items are' : 'item is'}`
          )
          .replace(
            '<TYPE>',
            `${entryType == EntryType.OUT ? 'available' : 'remaining'}`
          )
        dispatch({
          type: 'ERROR_MAX_LIMIT',
          payload: { name: name, value: selectedItem.availableQuantity },
          helperText,
        })
        setItem((item) => {
          return {
            ...item,
            [name]: selectedItem.availableQuantity,
          }
        })
      } else {
        dispatch({ type: 'SET', payload: { name: name, value: value } })
      }
    } else {
      dispatch({ type: 'ERROR', payload: { name: name, value: value } })
    }
  }

  const onAutocompleteInputChange = (e) => {
    const { value } = e.target
    setInputValue(value)
  }

  const quantityTextFieldRef = useRef(null)
  const amountTextFieldRef = useRef(null)
  const chargeTitleTextFieldRef = useRef<HTMLInputElement>(null)

  const onSelectChange = (
    e: any,
    newValue: {
      label: string
      id: number
      rate?: number
      availableQuantity?: number
      category: string
      categoryId?: number
      outAreaEntryId?: number
      isArea?: boolean
      isAreaV2?: boolean
    }
  ) => {
    // Check if "Add New Item" option was selected
    if (newValue?.id === -1) {
      // Close the dialog if callback provided
      if (onCloseDialog) {
        onCloseDialog()
      }
      // Navigate to item category page with query parameter to open Add Category dialog
      const targetPath = `${ApplicationLinks.stockItemCategoryLink()}?openAddCategory=true`
      navigate(targetPath)
      setValue(null)
      setInputValue('')
      return
    }

    setValue(newValue)
    setInputValue(newValue?.label ? newValue?.label : '')

    const updatedItem = newValue
    const isOtherChargesSelection = Boolean(
      otherChargesAmountUi &&
        updatedItem &&
        isOtherChargesCategory(updatedItem.category)
    )
    const isGenericOtherChargesSelection =
      isOtherChargesSelection && isReusableOtherChargeOption(updatedItem)
    const eligibleRentCycle =
      Boolean(
        enableRentCyclePlan &&
          entryType === EntryType.OUT &&
          submission_type === 'SUBMIT' &&
          updatedItem &&
          !updatedItem.isArea &&
          !updatedItem.isAreaV2 &&
          updatedItem.id > 0 &&
          !isOtherChargesSelection
      )
    let nextRentCycleMode: RentCycleMode = 'DAILY'
    if (eligibleRentCycle) {
      if (isPerSqftBill) {
        nextRentCycleMode = 'DAILY'
      } else {
        const cached = readRentCycleCache(updatedItem.categoryId ?? 0)
        nextRentCycleMode = cached?.mode === 'MONTHLY' ? 'MONTHLY' : 'DAILY'
      }
    }
    const initialRate = isOtherChargesSelection
      ? updatedItem?.rate ?? null
      : eligibleRentCycle && nextRentCycleMode === 'MONTHLY'
        ? null
        : getAutofillRateForDaily(updatedItem)

    setSelectedItem(updatedItem)
    setChargeTitle('')
    setChargeTitleError(false)
    setItem((item) => {
      const base = {
        ...item,
        entryType,
        quantity: 0,
        rate: initialRate,
        item: {
          ...item?.item,
          id: updatedItem?.id,
          label: updatedItem?.label,
          category: updatedItem?.category,
        },
        outAreaEntryId: updatedItem?.outAreaEntryId,
        excessQuantity: undefined,
      }
      if (isOtherChargesSelection) {
        delete base.siteTransferRate
      } else {
        base.siteTransferRate = updatedItem?.rate
      }
      return base
    })
    if (newValue?.id) {
      dispatch({ type: 'SET', payload: { name: 'item', value: newValue?.id } })
      dispatch({
        type: 'RESET',
        payload: { name: 'item', value: newValue?.id },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'quantity', value: 0 },
        helperText: suppressAvailableHelper
          ? initialItemIssueState.quantity.helperText
          : `${updatedItem.availableQuantity} ${
              updatedItem.isAreaV2 ? 'sqft available' : 
              entryType == EntryType.OUT
                ? 'available'
                : selectedDate
                ? `remaining (as of ${selectedDate})`
                : 'remaining (as of selected date)'
            }`,
      })
    } else {
      dispatch({ type: 'ERROR', payload: { name: 'item', value: null } })
    }
    if (eligibleRentCycle && nextRentCycleMode === 'MONTHLY' && newValue?.id) {
      dispatch({ type: 'SET', payload: { name: 'rate', value: '' } })
    }
    if (isGenericOtherChargesSelection) {
      setTimeout(() => chargeTitleTextFieldRef.current?.focus(), 0)
    } else if (isOtherChargesSelection) {
      setTimeout(() => amountTextFieldRef.current?.focus(), 0)
    } else {
      quantityTextFieldRef.current?.focus()
    }

    if (eligibleRentCycle) {
      setRentCycleMode(nextRentCycleMode)
    }
  }

  useEffect(() => {
    console.log('item=-=-=-=-=-=-=', item)
  }, [item])

  const rateFieldValue =
    showRentCycle &&
    rentCycleMode === 'DAILY' &&
    billType === 'PER_SQFT' &&
    (!selectedItem || selectedItem.category !== 'Sell Items')
      ? 0
      : item?.rate || item?.rate === 0
      ? item?.rate
      : ''

  const mdValue =
    entryType === EntryType.OUT || submission_type === 'LOST' ? 3.5 : 3.5

  // Prepare options array - include "Add New Item" when no items are available
  const options = selectOptions.length === 0 ? [ADD_NEW_ITEM_OPTION] : selectOptions

  return (
    <Grid
      container
      spacing={0}
      xs={12}
      sx={{ backgroundColor: 'white', paddingBottom: 0, mb: 0 }}
    >
      <Grid
        item={true}
        spacing={0}
        xs={12}
        md={mdValue}
        sx={{ backgroundColor: 'white', alignItems: 'left' }}
      >
        <Autocomplete
          size="small"
          sx={{ backgroundColor: 'white', padding: 0, margin: 0 }}
          disablePortal
          id="combo-box-demo"
          value={value}
          inputValue={inputValue}
          options={options}
          getOptionLabel={(option) => option.label || ''}
          onChange={onSelectChange}
          openOnFocus={true}
          renderOption={(props, option) => {
            // Special rendering for "Add New Item" option
            if (option.id === -1) {
              return (
                <Grid component={'li'} container {...props} key="add-new-item">
                  <Grid item xs pl={1} pr={1} pt={0.7} pb={0.7}>
                    <Box sx={{ color: 'green', fontWeight: 'bold' }}>
                      {option.label}
                    </Box>
                  </Grid>
                </Grid>
              )
            }

            const bgColor = option.rate === 0 ? '#eb627a' : '#314d67'
            const type = option.rate === 0 ? 'per sqft' : 'per item'
            const ignoreRates = billInfo?.id ?? 0 > 90882
            const showTag = !ignoreRates && billType === 'MIX' && entryType === 'IN';
            console.log('##############'+ignoreRates+'##########'+showTag+'#######'+billInfo?.id);
            const id =
              showTag
                ? `${option.label} ${type}`
                : `${option.label}`
            const outAreaEntryIdKey = option.outAreaEntryId
              ? `-${option.outAreaEntryId}`
              : ''
            const key = `${option.id}-${option.rate}${outAreaEntryIdKey}`
            return (
              <Grid component={'li'} container {...props} key={key}>
                <Grid id={id} item xs pl={1} pr={1} pt={0.7} pb={0.7}>
                  {option.label}
                </Grid>
                {showTag && (
                  <Grid item width={100} textAlign="right" p={1}>
                    <span
                      style={{
                        paddingLeft: 7,
                        paddingRight: 7,
                        paddingTop: 2,
                        paddingBottom: 2,
                        borderRadius: 20,
                        backgroundColor: bgColor,
                        color: 'white',
                      }}
                    >
                      {type}
                    </span>
                  </Grid>
                )}
              </Grid>
            )
          }}
          renderInput={(params) => (
            <TextField
              style={{ width: '100%', marginTop: 0 }}
              size="small"
              {...params}
              label="Select Item"
              inputRef={autocompleteRef}
              required
              onChange={onAutocompleteInputChange}
              error={itemData['item']['error']}
              helperText={itemData['item']['helperText']}
              sx={{ margin: 0 }}
            />
          )}
        />
      </Grid>

      {selectedItem?.isArea && (
        <>
         <Grid item={true} xs={12} pl={HORZ_SPACING} md={3.5 / 2.0}>
            <CustomNumberField
              style={{ width: '100%', margin: 0 }}
              maxLength={6}
              name="length"
              InputLabelProps={{ shrink: !!item?.length }}
              value={item?.length ? item?.length : ''}
              label="Length"
              onChange={onInputChange}
              onKeyDown={handleKeyDown}
              error={itemData['length']['error']}
              helperText={itemData['length']['helperText']}
              sx={{ width: '15ch' }}
              required
            ></CustomNumberField>
          </Grid>

          <Grid item={true} xs={12} md={3.5 / 2.0} pl={HORZ_SPACING}>
            <CustomNumberField
              style={{ width: '100%', margin: 0 }}
              maxLength={6}
              name="height"
              InputLabelProps={{ shrink: !!item?.height }}
              value={item?.height ? item?.height : ''}
              label="Height"
              onChange={onInputChange}
              onKeyDown={handleKeyDown}
              error={itemData['height']['error']}
              helperText={itemData['height']['helperText']}
            ></CustomNumberField>
          </Grid>
         
         <Grid item={true} xs={12} md={3.5 / 2.0} pl={HORZ_SPACING}>
            <CustomNumberField
              style={{ width: '100%', margin: 0 }}
              maxLength={6}
              name="width"
              InputLabelProps={{ shrink: !!item?.width }}
              value={item?.width ? item?.width : ''}
              label="Width"
              onChange={onInputChange}
              onKeyDown={handleKeyDown}
              error={itemData['width']['error']}
              helperText={itemData['width']['helperText']}
              sx={{ width: '15ch' }}
              required
            ></CustomNumberField>
          </Grid>

          {/* Quantity field for isAreaV2 items */}
          {selectedItem?.isAreaV2 && (
            <Grid item={true} xs={12} pl={HORZ_SPACING} md={mdValue}>
              <CustomNumberField
                maxLength={6}
                fullWidth
                name="quantity"
                InputLabelProps={{ shrink: !!item?.quantity }}
                value={item?.quantity ? item?.quantity : ''}
                label="sqft"
                onChange={onInputChange}
                onKeyDown={handleKeyDown}
                inputRef={quantityTextFieldRef}
                error={itemData['quantity']['error']}
                helperText={itemData['quantity']['helperText']}
                required
                style={{ width: '100%', marginTop: 0 }}
              ></CustomNumberField>
            </Grid>
          )}
         
        </>
      )}

      {isOtherChargesChallanLine && (
        <>
          {isGenericOtherChargeChallanLine && (
            <Grid item={true} xs={12} pl={HORZ_SPACING} md={mdValue}>
              <TextField
                size="small"
                fullWidth
                name="chargeTitle"
                value={chargeTitle}
                label="Charge Title"
                required
                inputRef={chargeTitleTextFieldRef}
                error={chargeTitleError}
                helperText={chargeTitleError ? 'Please enter charge title' : ' '}
                onChange={(e) => {
                  setChargeTitle(e.target.value)
                  setChargeTitleError(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    amountTextFieldRef.current?.focus()
                  }
                }}
                style={{ width: '100%', marginTop: 0 }}
              />
            </Grid>
          )}
          <Grid item={true} xs={12} pl={HORZ_SPACING} md={mdValue}>
            <CustomNumberField
              maxLength={8}
              fullWidth
              name="rate"
              InputLabelProps={{ shrink: !!item?.rate || item?.rate === 0 }}
              value={item?.rate || item?.rate === 0 ? item?.rate : ''}
              label="Amount"
              onChange={onInputChange}
              onKeyDown={handleKeyDown}
              inputRef={amountTextFieldRef}
              error={itemData['rate']['error']}
              helperText={itemData['rate']['helperText']}
              required
              style={{ width: '100%', marginTop: 0 }}
            />
          </Grid>
        </>
      )}

      {!isOtherChargesChallanLine &&
        !selectedItem?.isArea &&
        !selectedItem?.isAreaV2 &&
        (showRentCycle ? (
          <Grid item={true} xs={12} pl={HORZ_SPACING} md={mdValue}>
            <CustomNumberField
              maxLength={6}
              fullWidth
              name="quantity"
              InputLabelProps={{ shrink: !!item?.quantity }}
              value={item?.quantity ? item?.quantity : ''}
              label={quantityLabelOverride || 'Quantity'}
              onChange={onInputChange}
              onKeyDown={handleKeyDown}
              inputRef={quantityTextFieldRef}
              error={itemData['quantity']['error']}
              helperText={itemData['quantity']['helperText']}
              required
              style={{ width: '100%', marginTop: 0 }}
            ></CustomNumberField>
          </Grid>
        ) : (
          <Grid item={true} xs={12} pl={HORZ_SPACING} md={mdValue}>
            <CustomNumberField
              maxLength={6}
              fullWidth
              name="quantity"
              InputLabelProps={{ shrink: !!item?.quantity }}
              value={item?.quantity ? item?.quantity : ''}
              label={quantityLabelOverride || 'Quantity'}
              onChange={onInputChange}
              onKeyDown={handleKeyDown}
              inputRef={quantityTextFieldRef}
              error={itemData['quantity']['error']}
              helperText={itemData['quantity']['helperText']}
              required
              style={{ width: '100%', marginTop: 0 }}
            ></CustomNumberField>
          </Grid>
        ))}

      {/* Quantity field for isAreaV2 items (when not in isArea section) */}
      {selectedItem?.isAreaV2 && !selectedItem?.isArea && (
        <Grid item={true} xs={12} pl={HORZ_SPACING} md={mdValue}>
          <CustomNumberField
            maxLength={6}
            fullWidth
            name="quantity"
            InputLabelProps={{ shrink: !!item?.quantity }}
            value={item?.quantity ? item?.quantity : ''}
            label="sqft"
            onChange={onInputChange}
            onKeyDown={handleKeyDown}
            inputRef={quantityTextFieldRef}
            error={itemData['quantity']['error']}
            helperText={itemData['quantity']['helperText']}
            required
            style={{ width: '100%', marginTop: 0 }}
          ></CustomNumberField>
        </Grid>
      )}

      {/* Rate (non–rent-cycle) or Rent cycle only */}
      {!isOtherChargesChallanLine &&
        (entryType === EntryType.OUT || submission_type === 'LOST') &&
        !selectedItem?.isAreaV2 &&
        (showRentCycle ? (
          <>
            <Grid item={true} xs={12} md={mdValue / 2} pl={HORZ_SPACING}>
              <CustomNumberField
                style={{ width: '100%', margin: 0 }}
                maxLength={6}
                id={`id_typo_textfield_price`}
                name="rate"
                value={rateFieldValue}
                label="Rate"
                onKeyDown={handleKeyDown}
                onChange={onInputChange}
                error={itemData['rate']['error']}
                helperText={itemData['rate']['helperText']}
              ></CustomNumberField>
            </Grid>
            <Grid item={true} xs={12} md={mdValue / 2} pl={HORZ_SPACING}>
              <FormControl fullWidth size="small" sx={{ mt: 0 }}>
                <InputLabel id="rent-cycle-select-label">Rent cycle</InputLabel>
                <Select
                  labelId="rent-cycle-select-label"
                  label="Rent cycle"
                  value={rentCycleMode}
                  onChange={onRentCycleChange}
                  disabled={isPerSqftBill}
                >
                  <MenuItem value="DAILY">Daily</MenuItem>
                  <MenuItem value="MONTHLY">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        ) : (
          <Grid
            item={true}
            xs={12}
            md={selectedItem?.isArea ? mdValue / 2.0 : mdValue}
            pl={HORZ_SPACING}
          >
            <CustomNumberField
              style={{ width: '100%', margin: 0 }}
              maxLength={6}
              id={`id_typo_textfield_price`}
              name="rate"
              value={
                billType === 'PER_SQFT' &&
                submission_type !== 'LOST' &&
                (!selectedItem || selectedItem.category !== 'Sell Items')
                  ? 0
                  : item?.rate || item?.rate === 0
                  ? item?.rate
                  : ''
              }
              label={submission_type === 'LOST' ? 'Total Cost' : 'Rate'}
              onKeyDown={handleKeyDown}
              onChange={onInputChange}
              error={
                itemData[submission_type === 'LOST' ? 'total_cost' : 'rate'][
                  'error'
                ]
              }
              helperText={itemData['rate']['helperText']}
            ></CustomNumberField>
          </Grid>
        ))}

      {/* Excess Quantity (Receiving Challan only, optional) */}
      {entryType === EntryType.IN &&
        submission_type !== 'LOST' &&
        !selectedItem?.isArea &&
        !isOtherChargesChallanLine &&
        !ignoreAvailableQuantity && (
        <Grid item={true} xs={12} pl={HORZ_SPACING} md={mdValue}>
          
          <CustomNumberField
            maxLength={5}
            fullWidth
            name="excessQuantity"
            InputLabelProps={{ shrink: !!item?.excessQuantity }}
            value={item?.excessQuantity ?? ''}
            label="Excess Quantity"
            onChange={(event: any) => {
              const raw = event?.target?.value
              const num = raw === '' || raw == null ? undefined : Number(raw)
              setItem((prev) => ({ ...prev, excessQuantity: num }))
            }}
            onKeyDown={handleKeyDown}
            style={{ width: '100%', marginTop: 0 }}
          />
        </Grid>
      )}

      <Grid
        item={true}
        md={1.5}
        xs={12}
        sx={{ backgroundColor: 'white', pl: HORZ_SPACING }}
      >
        <Box>
          <Button
            fullWidth
            sx={{
              mt: {
                height: 37.5,
                backgroundColor: 'rgb(0, 128, 0, 0.5)',
                color: 'green',
                borderRadius: 4,
              },
            }}
            size="small"
            id='button_add_item_in_challan'
            data-cy='button_add_item_in_challan'
            onClick={addItemHandler}
            startIcon={<AddTwoToneIcon />}
          >
            Add
          </Button>
        </Box>
      </Grid>
    </Grid>
  )
}
export default ItemComponent
